import { useState } from 'react';
import { useNavigate, Link, Navigate } from 'react-router';
import { STORAGE_KEYS } from '@vvf/shared';
import { api, setAccessToken } from '@/services/api';
import { loginSchema, formatZodErrors } from '@/shared/validation';
import { FormError } from '@/shared/FormError';
import { notifyNativeLogin } from '@/services/nativeBridge';
import { useAuth } from '@/shared/AuthContext';

export function LoginPage(): React.JSX.Element {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  // US-286: true quando il 403 ha code=email_not_verified -> offriamo il
  // reinvio del link di conferma direttamente da qui.
  const [needsVerification, setNeedsVerification] = useState(false);
  const [resendDone, setResendDone] = useState(false);
  const navigate = useNavigate();
  const { user, booting, setUser } = useAuth();

  // Item #6: il gate non legge piu' il token da localStorage (in cookie mode
  // l'access vive in memoria). Usa lo stato `user` di AuthContext popolato dal
  // silent-refresh al boot. Tutti gli hook sono chiamati PRIMA del return
  // condizionale per rispettare le rules-of-hooks.
  if (booting) {
    return <div className="spinner-overlay" />;
  }
  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setFieldErrors({});
    setNeedsVerification(false);
    setResendDone(false);

    const parsed = loginSchema.safeParse({ email, password, rememberMe });
    if (!parsed.success) {
      setFieldErrors(formatZodErrors(parsed));
      return;
    }

    setLoading(true);
    try {
      const res = await api.post('/auth/login', parsed.data);
      setAccessToken(res.data.accessToken);
      // Audit #2: in browser puro (cookie mode) il refresh token e' nel
      // cookie httpOnly, NON in localStorage (mitigazione XSS). Il backend
      // ritorna refreshToken=null quando legge X-Auth-Mode: cookie. In
      // wrapper RN il refresh token e' invece nel body (gestito separato).
      if (res.data.refreshToken) {
        localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, res.data.refreshToken);
      }
      // setUser aggiorna React state (tutti i useAuth consumer re-render)
      // + localStorage atomicamente. Vedi audit #8.
      setUser(res.data.user);
      // Se siamo dentro il wrapper nativo, notifica login per registrare FCM token
      notifyNativeLogin(res.data.accessToken, res.data.refreshToken, res.data.user);
      navigate('/dashboard');
    } catch (err: unknown) {
      // error = shape custom {error}; detail = ProblemDetails (403 approvazione
      // / email non confermata); code = discriminante machine-readable US-286.
      const data = (err as { response?: { data?: { error?: string; detail?: string; code?: string } } })?.response?.data;
      setError(data?.error ?? data?.detail ?? 'Credenziali non valide');
      setNeedsVerification(data?.code === 'email_not_verified');
    } finally {
      setLoading(false);
    }
  }

  async function handleResendVerification() {
    try {
      await api.post('/auth/resend-verification', { email });
    } finally {
      // Risposta sempre identica lato server (anti-enumeration): allineati.
      setResendDone(true);
    }
  }

  return (
    <div className="login-shell">
      <div className="login-container">
        <div className="login-logo">
          <img src="/icon-128.webp" alt="VVF"
            style={{ width: 72, height: 72, margin: '0 auto 10px', display: 'block' }} />
          <h1>VVF Tracker</h1>
          <p>Accedi al tuo account</p>
        </div>

        <div className="login-form-wrap">
          <h2>Accedi</h2>
          {error && <div className="error-message">{error}</div>}
          {needsVerification && !resendDone && (
            <button type="button" onClick={handleResendVerification}
              className="submit-btn"
              style={{ marginBottom: 12, background: 'linear-gradient(135deg, #fd7e14 0%, #d96a0b 100%)' }}>
              ✉️ Reinvia email di conferma
            </button>
          )}
          {resendDone && (
            <div style={{ background: '#d4edda', color: '#155724', padding: 12, borderRadius: 8, fontSize: 13, marginBottom: 12 }}>
              Se l'email esiste e non e' ancora confermata, riceverai un nuovo link.
            </div>
          )}

          <form onSubmit={handleSubmit} noValidate>
            <div className="form-group">
              <label htmlFor="email">Email</label>
              <input id="email" type="email" value={email}
                onChange={(e) => setEmail(e.target.value)} autoFocus />
              <FormError message={fieldErrors.email} />
            </div>

            <div className="form-group">
              <label htmlFor="password">Password</label>
              <input id="password" type="password" value={password}
                onChange={(e) => setPassword(e.target.value)} />
              <FormError message={fieldErrors.password} />
            </div>

            <div className="checkbox-group">
              <input id="rememberMe" type="checkbox" checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)} />
              <label htmlFor="rememberMe">Ricordami</label>
            </div>

            <button type="submit" className="submit-btn" disabled={loading}>
              {loading ? 'Accesso in corso...' : 'Accedi'}
            </button>

            {/* Bryan: bottone "Mappa idranti" grande come Accedi, verticale sotto */}
            <button type="button" onClick={() => navigate('/hydrants-public')}
              className="submit-btn"
              style={{ marginTop: 10,
                background: 'linear-gradient(135deg, #dc3545 0%, #a71d2a 100%)' }}>
              💧 Mappa Idranti (accesso pubblico)
            </button>
          </form>

          <div className="form-footer">
            <Link to="/register">Registrati</Link>
            <span style={{ margin: '0 8px', color: '#ccc' }}>·</span>
            <Link to="/forgot-password">Password dimenticata?</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
